import React, { useCallback, useState } from "react";
import { InputAdornment, IconButton, Box, TextField, Button, Popover, List, ListItem, Typography, Chip } from "@mui/material"
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ArrowDropDownCircleRoundedIcon from "@mui/icons-material/ArrowDropDownCircleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { alertError } from "../Alert";
import isFileOverSize from "utils/isFileOverSize";
import { BoxFC } from "components/BoxCustom";
import { SystemUpdateAltRounded } from "resource/muiImport";
import { grey } from "@mui/material/colors";
const FileUpload = ({ options, fileUploadData, setFileUploadData, width, height, sx, compact = false }) => {

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [fileName, setFileName] = useState("")
  const [selectedFile, setSelectedFile] = useState(null)

  const handleAddFile = useCallback((file, isMulti) => {
   
    if (fileName === "" && !isMulti) {
      alertError("กรุณาใส่ชื่อไฟล์ ก่อน");
      return;
    }
    setFileUploadData(oldState => {
     
     
      return ({
        ...oldState,
        addedFiles: [
          ...oldState.addedFiles,
          {
            file: file,
            fileNm: isMulti?file.name: fileName,
            label: isMulti?file.name: fileName,
            isEdit: false,
            imgLink: URL.createObjectURL(file)
          }
        ]
      })
    });
    setSelectedFile(null);
    setFileName("");
  }, [fileName, setFileUploadData])

  const handleListItemClick = (item) => {
    setFileName(item);
    setAnchorEl(null);
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
   
    if (e.dataTransfer.items) {
      if (e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === 'file') {
        const file = e.dataTransfer.items[0].getAsFile();
        if (isFileOverSize(file)) return
       
       
        setSelectedFile(file)
        const fileNameOnly = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
        setFileName(fileNameOnly)
      }
    } else {
      if (e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0]
        if (isFileOverSize(file)) return
        setSelectedFile(file)
        const fileNameOnly = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
        setFileName(fileNameOnly)
      }
    }
  }, [])
  const handleDragOver = useCallback((e) => {
    /**Must prevent then can use onDrop 
     * https://stackoverflow.com/questions/50230048/react-ondrop-is-not-firing
     */
    e.preventDefault()
  }, [])

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (isFileOverSize(file)) return;
        handleAddFile(file, true);
      }
    }
  }, [handleAddFile]);

  const handleDropMulti = useCallback( (e) =>{
    e.preventDefault()
    let files = []
    if (e.dataTransfer.items) {
      if (e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === 'file') {
        files = Array.from(e.dataTransfer.items).map(item => item.getAsFile())
      } else {
        return
      }
    } else {
      if (e.dataTransfer.files.length > 0) {
        files = Array.from(e.dataTransfer.files)
      } else {
        return
      }
    }

    for (const file of files) {
      if (isFileOverSize(file)) return
      handleAddFile(file, true)
    }
    
  }, [handleAddFile])
  const handleSelectFile = useCallback((e) => {
    const file = e.target.files[0]
    if (isFileOverSize(file)) return

    setSelectedFile(file)
    setFileName(file.name)
  }, [])

  // touch layout: a picked file is added right away, named by the chosen preset or by its own name
  const handlePickCompact = useCallback((e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (files.some(file => isFileOverSize(file))) return
    if (files.length === 1 && fileName) {
      handleAddFile(files[0], false)
    } else {
      for (const file of files) handleAddFile(file, true)
    }
  }, [fileName, handleAddFile])

  if (compact) {
    const numFile = fileUploadData.serverFiles.length + fileUploadData.addedFiles.length
    return (
      <BoxFC sx={{ gap: 1.25, width: "100%", ...sx }}>
        {options?.length > 0 ?
          <Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.75 }}>
              ชื่อไฟล์ (เลือกก่อนถ่ายรูป/เลือกไฟล์ หรือไม่เลือกก็ได้)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {options.map(item => (
                <Chip key={item} label={item} sx={{ height: 36, fontSize: 14 }}
                  color={fileName === item ? "primary" : "default"}
                  variant={fileName === item ? "filled" : "outlined"}
                  aria-pressed={fileName === item}
                  onClick={() => setFileName(o => o === item ? "" : item)} />
              ))}
            </Box>
          </Box>
          : null}
        <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <Button variant="contained" component="label" startIcon={<PhotoCameraRoundedIcon />} sx={{ minHeight: 44 }}>
            ถ่ายรูป
            <input type="file" hidden accept="image/*" capture="environment" onChange={handlePickCompact} />
          </Button>
          <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />} sx={{ minHeight: 44 }}>
            เลือกไฟล์
            <input type="file" hidden multiple onChange={handlePickCompact} />
          </Button>
        </Box>
        {numFile === 0 ?
          <Typography sx={{ fontSize: 14, color: "text.secondary", textAlign: "center", py: 1.5, bgcolor: grey[100], borderRadius: 2 }}>
            ยังไม่มีไฟล์
          </Typography>
          :
          <Box>
            {fileUploadData.serverFiles.map((item, index) => (
              <FileItem key={`s${index}`} index={index} file={item} compact
                setFileUploadData={setFileUploadData} fileMode={"serverFiles"} />
            ))}
            {fileUploadData.addedFiles.map((item, index) => (
              <FileItem key={`a${index}`} index={index} file={item} compact
                setFileUploadData={setFileUploadData} fileMode={"addedFiles"} />
            ))}
          </Box>
        }
      </BoxFC>
    )
  }

  return (

    <Box display="flex" flexDirection="column" gap={1} width={width || "100%"} height={height || "100%"} alignItems="flex-start" sx={sx} >
      <Box display="flex" flexDirection="row" gap={1}>


        <TextField
          onDrop={handleDrop}
          label="ชื่อไฟล์"
          value={fileName}
          onChange={e => setFileName(e.target.value)}
          sx={{ flexGrow: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" >
                <IconButton disableRipple={true} sx={{ p: 0 }} edge="end" onClick={e => setAnchorEl(e.currentTarget)}>
                  <ArrowDropDownCircleRoundedIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          variant="outlined"
          size="small"
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={(e) => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}>
          <List dense>
            {options.map((item, index) => (
              <ListItem key={index}><Button variant="outlined" fullWidth onClick={() => handleListItemClick(item)}>{item}</Button></ListItem>
            ))}
          </List>
        </Popover>
        {
          selectedFile ?
            <Box display="flex" gap={1}>
              <Button variant="contained" color="success" size="small" sx={{ minWidth: 20, width: 40 }} onClick={() => handleAddFile(selectedFile)}><DoneRoundedIcon /></Button>
              <Button variant="contained" color="error" size="small" sx={{ minWidth: 20, width: 40 }} onClick={() => setSelectedFile(null)}><CloseRoundedIcon /></Button>
            </Box>
            :
            <Button variant="contained" size="small" sx={{ width: 90 }} component="label">
              <input type="file" hidden onChange={handleSelectFile} />
              เลือกไฟล์
            </Button>
        }
      </Box>
      <Box display="flex" flexGrow={1} width="100%"
        sx={{ borderRadius: 2, p: 2, outline: "none", "&:focus": { border: "2px solid #1976d2" } }}
        bgcolor={grey[200]} flexDirection="column" tabIndex={0}
        onPaste={handlePaste} onDrop={handleDropMulti} onDragOver={handleDragOver}>
        {
          fileUploadData.serverFiles.length === 0 && fileUploadData.addedFiles.length === 0 ?

            <BoxFC sx={{ gap: 0 }} justifyContent="center" alignItems="center" width="100%" height="100%" >
              <SystemUpdateAltRounded sx={{ fontSize: 50, color: grey[500] }} />
              <Typography variant="h6" sx={{ color: grey[500], textAlign: "center" }}>Drop file or<br/>Paste image here </Typography>
            </BoxFC>
            :
            [
              ...fileUploadData.serverFiles.map((item, index) => (
                <FileItem key={`s${index}`} index={index} file={item}
                  setFileUploadData={setFileUploadData}
                  fileMode={"serverFiles"} />
              )),
              ...fileUploadData.addedFiles.map((item, index) => (
                <FileItem key={`a${index}`} index={index} file={item}
                  setFileUploadData={setFileUploadData}
                  fileMode={"addedFiles"} />
              ))
            ]
        }
        {
          // fileUploadData.addedFiles.map((item, index) => (
          //   <FileItem key={`a${index}`} index={index} file={item}
          //     setFileUploadData={setFileUploadData}
          //     fileMode={"addedFiles"} />
          // ))
        }
      </Box>
    </Box>
  )
}

export default React.memo(FileUpload)

const FileItem = ({ index, file, fileMode, setFileUploadData, compact }) => {
  // touch layout gets 40px buttons
  const btnSize = compact ? 40 : 30

  const { label, isEdit } = file;
  const [tfValue, setTFValue] = useState("");

  const handleEditclick = () => {
   
    if (!isEdit) {
      setTFValue(label);
    }
    file.isEdit = !isEdit;
    file.label = isEdit ? tfValue : label
    setFileUploadData(oldState => ({ ...oldState }))
  }

  const handleDeleteClick = () => {
    if (isEdit) {
      file.isEdit = false;
      file.label = label;
      setFileUploadData(oldState => ({ ...oldState }));
    } else {
      setFileUploadData(oldState => ({
        ...oldState,
        [fileMode]: oldState[fileMode].filter(item => item !== file),
        deletedFiles: fileMode === "serverFiles" ? [...oldState.deletedFiles, file] : [...oldState.deletedFiles]
      }));
    }
  }
  // console.log('Rendering FileItem:', index, file);
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap={1} mb={1}>
      {
        isEdit ?
          <TextField size="small"
            value={tfValue} onChange={e => setTFValue(e.target.value)}
            sx={{ bgcolor: "white", flexGrow: 1 }}
            inputProps={{ style: compact ? { fontSize: 16 } : { fontSize: "0.8rem", height: 12 } }}
          />
          :
          <Button variant="outlined" size="small" disabled={false}
            sx={{ flexGrow: 1, minHeight: btnSize,
              textTransform: "none",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            onClick={() => window.open(file.imgLink)}><span style={{ overflow: "hidden", textOverflow: "ellipsis"}}>{label}</span></Button>
      }
      <Button variant="contained" size="small" sx={{ minWidth: 20, width: btnSize, height: btnSize }} aria-label={isEdit ? "ยืนยันชื่อไฟล์" : "แก้ชื่อไฟล์"} onClick={handleEditclick}>
        {isEdit ? <DoneRoundedIcon /> : <EditRoundedIcon />}
      </Button>
      <Button variant="contained" color="error" size="small" sx={{ minWidth: 20, width: btnSize, height: btnSize }} aria-label={isEdit ? "ยกเลิกแก้ชื่อ" : "ลบไฟล์"} onClick={handleDeleteClick}>
        {isEdit ? <CloseRoundedIcon /> : <DeleteRoundedIcon />}
      </Button>
    </Box>
  )
}